import { useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import {
  Activity,
  Award,
  BarChart3,
  BookOpen,
  ChevronDown,
  GraduationCap,
  LayoutDashboard,
  Layers,
  Lightbulb,
  Menu,
  ShieldCheck,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { ProfilePopover } from "./ProfilePopover";
import { useProfile } from "../hooks/useProfile";
import { useIsAdmin } from "../hooks/useIsAdmin";
import { useNav } from "../nav/NavProvider";
import { BUILT_IN } from "../nav/navConfig";

type PageMeta = { title: string; icon: LucideIcon };

const PAGE_META: Record<string, PageMeta> = {
  "/":            { title: "AI Dashboard",   icon: LayoutDashboard },
  "/dashboard-new": { title: "AI Dashboard (New)", icon: LayoutDashboard },
  "/library":     { title: "AI Library",     icon: BookOpen },
  "/initiatives": { title: "AI Initiatives", icon: Lightbulb },
  "/platforms":   { title: "AI Platforms",   icon: Layers },
  "/training":    { title: "AI Training",    icon: GraduationCap },
  "/insights":    { title: "Insights",       icon: BarChart3 },
  "/admin":       { title: "Admin",          icon: ShieldCheck },
  "/activity":    { title: "Activity Log",   icon: Activity },
  "/champions-new": { title: "AI Champions (New)", icon: Award },
};

const DEFAULT_META: PageMeta = { title: "AI Navigator Hub", icon: LayoutDashboard };

type Props = {
  onMobileMenuToggle: () => void;
  onSignOut: () => void;
};

function initials(name?: string) {
  if (!name) return "?";
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() ?? "")
    .join("");
}

export function Header({ onMobileMenuToggle, onSignOut }: Props) {
  const { accounts } = useMsal();
  const account = accounts[0];
  const name = account?.name ?? account?.username ?? "Signed in";
  const email = account?.username ?? "";
  const ini = initials(name);

  const { pathname } = useLocation();
  const meta = PAGE_META[pathname] ?? DEFAULT_META;
  // A page renamed in Admin → Navigation carries its new name up here too.
  const navEntry = useNav().config.find((e) => e.path === pathname);
  const renamed = navEntry && navEntry.label !== BUILT_IN.find((b) => b.id === navEntry.id)?.label;
  const pageTitle = renamed ? navEntry.label : meta.title;
  const PageIcon = meta.icon;

  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);
  const profile = useProfile();
  const isAdmin = useIsAdmin();

  return (
    <header className="app-header">
      <div className="app-header-inner">
        <button
          type="button"
          className="app-header-menu"
          aria-label="Open navigation"
          onClick={onMobileMenuToggle}
        >
          <Menu size={20} />
        </button>
        <div className="app-header-brand">
          <PageIcon size={18} strokeWidth={1.75} className="app-header-brand-icon" aria-hidden />
          <span className="app-header-brand-name">{pageTitle}</span>
        </div>
        <div className="header-spacer" />
        <button
          ref={triggerRef}
          type="button"
          className={`user-chip${open ? " is-open" : ""}`}
          title={email}
          aria-haspopup="dialog"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
        >
          <div className="user-avatar" aria-hidden>
            {profile.photoUrl ? (
              <img src={profile.photoUrl} alt="" />
            ) : (
              <span>{ini}</span>
            )}
          </div>
          <div className="user-meta">
            <span className="user-name">{name}</span>
            {email ? <span className="user-email">{email}</span> : null}
          </div>
          <ChevronDown size={14} className="user-chip-caret" aria-hidden />
        </button>
        <ProfilePopover
          open={open}
          anchor={triggerRef.current}
          state={profile}
          fallbackName={name}
          fallbackEmail={email}
          fallbackInitials={ini}
          isAdmin={isAdmin}
          onClose={() => setOpen(false)}
          onSignOut={onSignOut}
        />
      </div>
    </header>
  );
}
